import { ROUTESWEB } from "@repo/utils";
import type { ProfilesData } from "../../../store/useAuthStore";

const GENIUS_DEEPLINK_NAV_LOCK_KEY = "genius_deeplink_nav_lock_active";
const GENIUS_DEEPLINK_EXAM_ATTEMPT_KEY = "genius_deeplink_exam_attempt_active";

const testsBase = `/${ROUTESWEB.APP.TESTS}`;

/** Paths allowed before the student enters the exam. */
const GENIUS_DEEPLINK_PRE_EXAM_ALLOWED_PATHS = [
  `${testsBase}/genius-assessment-starting-soon`,
  `${testsBase}/genius-assessment-not-available`,
  `${testsBase}/general-instructions`,
  `${testsBase}/exam`,
] as const;

/** Paths allowed after the student has entered the exam attempt. */
const GENIUS_DEEPLINK_EXAM_ATTEMPT_ALLOWED_PATHS = [
  `${testsBase}/exam`,
  `${testsBase}/test-finish`,
  `${testsBase}/upload-answer-sheet`,
  `${testsBase}/upload-answer-pdf`,
  `${testsBase}/pdf-preview`,
  `${testsBase}/report`,
  `${testsBase}/calculation-score`,
  `${testsBase}/solutions`,
  `${testsBase}/subjective-solutions`,
  `/${ROUTESWEB.APP.REPORTS}`,
] as const;

function readFlag(key: string): boolean {
  try {
    if (localStorage.getItem(key) === "1") return true;
    // Migrate legacy sessionStorage flags from earlier web builds.
    if (sessionStorage.getItem(key) === "1") {
      localStorage.setItem(key, "1");
      sessionStorage.removeItem(key);
      return true;
    }
  } catch {
    // ignore
  }
  return false;
}

function writeFlag(key: string, enabled: boolean): void {
  try {
    if (enabled) {
      localStorage.setItem(key, "1");
    } else {
      localStorage.removeItem(key);
    }
    sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

function isPathInAllowedList(
  pathname: string,
  allowedPaths: readonly string[],
): boolean {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  return allowedPaths.some(
    (allowedPath) =>
      normalized === allowedPath || normalized.startsWith(`${allowedPath}/`),
  );
}

export function markGeniusDeeplinkNavLock(): void {
  writeFlag(GENIUS_DEEPLINK_NAV_LOCK_KEY, true);
}

export function isGeniusDeeplinkNavLockActive(): boolean {
  return readFlag(GENIUS_DEEPLINK_NAV_LOCK_KEY);
}

export function clearGeniusDeeplinkNavLock(): void {
  clearGeniusDeeplinkExamAttempt();
  writeFlag(GENIUS_DEEPLINK_NAV_LOCK_KEY, false);
}

export function markGeniusDeeplinkExamAttempt(): void {
  writeFlag(GENIUS_DEEPLINK_EXAM_ATTEMPT_KEY, true);
}

export function isGeniusDeeplinkExamAttemptActive(): boolean {
  return readFlag(GENIUS_DEEPLINK_EXAM_ATTEMPT_KEY);
}

export function clearGeniusDeeplinkExamAttempt(): void {
  writeFlag(GENIUS_DEEPLINK_EXAM_ATTEMPT_KEY, false);
}

export function isGeniusMobileVerified(
  profilesData: ProfilesData | null | undefined,
): boolean {
  const fromUserAccount = (
    profilesData?.unified_account_details?.user_account_details as
      | { is_phone_verified?: boolean }
      | undefined
  )?.is_phone_verified;
  if (typeof fromUserAccount === "boolean") {
    return fromUserAccount;
  }

  // Signed-token exchange may return is_phone_verified on the data root.
  const fromPayload = (
    profilesData as { is_phone_verified?: boolean } | null | undefined
  )?.is_phone_verified;
  if (typeof fromPayload === "boolean") {
    return fromPayload;
  }

  return true;
}

export function isPathAllowedDuringGeniusNavLock(pathname: string): boolean {
  const normalized = pathname.replace(/\/+$/, "") || "/";

  // Genius entry route itself is always allowed while the flow is active.
  // Also allow path-style variants (`/genius-deeplink/module=...&...`) until normalized.
  if (
    normalized === "/genius-deeplink" ||
    normalized.startsWith("/genius-deeplink/")
  ) {
    return true;
  }

  if (isGeniusDeeplinkExamAttemptActive()) {
    return isPathInAllowedList(
      pathname,
      GENIUS_DEEPLINK_EXAM_ATTEMPT_ALLOWED_PATHS,
    );
  }

  return isPathInAllowedList(pathname, [
    ...GENIUS_DEEPLINK_PRE_EXAM_ALLOWED_PATHS,
    ...GENIUS_DEEPLINK_EXAM_ATTEMPT_ALLOWED_PATHS,
  ]);
}

export function syncGeniusDeeplinkExamAttemptForPath(pathname: string): void {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  const examPath = `${testsBase}/exam`;
  const reportsPath = `/${ROUTESWEB.APP.REPORTS}`;

  if (normalized === examPath || normalized.startsWith(`${examPath}/`)) {
    markGeniusDeeplinkExamAttempt();
    return;
  }

  if (
    normalized === reportsPath ||
    normalized.startsWith(`${reportsPath}/`) ||
    normalized === `${testsBase}/report` ||
    normalized.startsWith(`${testsBase}/report/`)
  ) {
    markGeniusDeeplinkExamAttempt();
  }
}
